/**
 * 🎪 **행사 화상 방 이름이 짧으면 낯선 사람이 들어온다.**
 *
 * 공개 화상 서비스(jit.si)는 **방 이름만 맞히면 누구나 들어온다.** 수업 쪽은 이 사실을
 * 알고 긴 이름을 만들어 쓴다(지금은 `newJitsiRoom`) — 그런데 **행사 쪽은 운영자가 적은
 * 글자를 그대로 주소에 넣는다.** 운영에 실제로 `ㅁㅁㅁ` 이 들어가 있었다(세 글자).
 * 아이들이 얼굴을 켜고 들어가는 방이다.
 *
 * ## 🔴 이름을 **말없이 바꾸지는 않는다**
 * 운영자가 참가자에게 이미 방 이름을 알려 줬을 수 있다. 코드가 몰래 바꾸면 그날 아무도
 * 못 들어온다. 그래서 여기서는 **위험한지 알려 주기만** 하고, 고치는 것은 운영자가 한다.
 * (마지막 방어선은 진행자가 방에 들어가 잠그는 것이고, 화면도 그렇게 안내한다.)
 */

/** 방 이름이 이만큼은 돼야 무작위로 찍어 들어오기 어렵다. */
export const EVENT_ROOM_MIN = 12;

export type EventRoomRisk = "ok" | "empty" | "short" | "no_digit";

/** 이 방 이름이 안전한가 — 아니면 무엇이 문제인가. */
export function eventRoomRisk(name: unknown): EventRoomRisk {
  const s = typeof name === "string" ? name.trim() : "";
  if (!s) return "empty";
  if (s.length < EVENT_ROOM_MIN) return "short";
  if (!/[0-9]/.test(s)) return "no_digit";
  return "ok";
}

/** 운영자에게 할 말 — 무엇이 문제이고 어떻게 고치는지까지. */
export function eventRoomSay(risk: EventRoomRisk): string | null {
  switch (risk) {
    case "empty":
      return null; // 화상 무대를 안 쓰는 행사다 — 잔소리하지 않는다
    case "short":
      return `방 이름이 너무 짧아요(${EVENT_ROOM_MIN}자 이상). 이름만 맞히면 누구나 들어오는 공개 화상방이라, 아이들 방은 길게 지어 주세요. 예) greg-chick-booktalk-1115`;
    case "no_digit":
      return "방 이름에 숫자를 섞어 주세요. 낱말만으로 지으면 찍어서 들어오기 쉬워요. 예) greg-chick-booktalk-1115";
    default:
      return null;
  }
}

/**
 * 🎥 **이 행사의 화상 방이 위험한가** — 아이 화면에 나가는 행사만, 실제로 쓰는 방만.
 *
 * 🔴 두 가지를 함께 본다:
 *  ① **아이에게 보이는 행사인가**(`visible !== false`) — 안 켠 초안까지 세면 잔소리다.
 *  ② **그 방을 실제로 쓰나** — 운영자가 회의 주소(`meetUrl`)를 넣었으면 옛 Jitsi 방 이름은
 *     안 쓴다(`stageMeetUrl` 이 주소를 먼저 본다). 그런데도 경고하면 **안 쓰는 값으로
 *     잔소리**하는 것이고, 운영자는 고칠 수 없는 빨간 줄을 계속 본다 — 그러면
 *     목록 전체를 안 믿게 된다(2026-09-02).
 *
 * 판정을 여기 두는 이유: 화면에서 조건을 짜면 다음에 칸이 하나 늘 때 또 갈린다.
 */
export function eventRoomRisky(e: {
  visible?: boolean;
  stage?: { meetUrl?: string; jitsi?: string };
}): boolean {
  if (e.visible === false) return false;
  if (e.stage?.meetUrl) return false;
  const room = e.stage?.jitsi;
  return !!room && eventRoomRisk(room) !== "ok";
}

/**
 * 🧪 **아이 화면에 「테스트」 행사가 떠 있나.**
 *
 * ## 🔴 왜 (2026-08-20에 한 번 났고, 2026-09-04에 또 떠 있었다)
 * 개발용 데모 행사가 **「지금 열리고 있어요」로 운영에 떠 있었다.** 그때 고친 방법은
 * `visible` 스위치를 만든 것이었는데, 스위치는 **켜 두는 것을 막지 못한다.**
 *
 * 실제로 그 뒤에도 「북토크 테스트」가 `visible: true` 로 아이 화면에 떠 있었다
 * (운영자가 웨일온을 확인하려고 켠 것이다 — 잘못이 아니라 **끄는 것을 잊기 쉬운 것**이다).
 *
 * 아이와 보호자는 그 제목을 그대로 읽는다. 「테스트」라고 적힌 행사에 참가 신청을 하거나,
 * 그 시각에 들어와서 아무도 없는 방을 본다.
 *
 * ## 🔴 막지는 않는다 — **보이게만** 한다
 * 진짜로 「테스트」가 들어간 제목의 행사를 열 수도 있다(공개 리허설 등). 그래서
 * 이건 「반드시 고칠 것」이 아니라 **「이거 켜 둔 거 맞나요?」**다.
 */
const TEST_WORDS = /테스트|시험|데모|샘플|test|demo|sample|dummy/i;

/** 제목이 「임시로 만든 것」처럼 보이나. 판정을 화면마다 짜면 갈린다. */
export function looksLikeTestTitle(title: unknown): boolean {
  const s = typeof title === "string" ? title.trim() : "";
  return !!s && TEST_WORDS.test(s);
}

/**
 * 아이 화면에 보이는 **테스트처럼 보이는** 행사인가.
 *
 * 🔴 `visible !== false` 인 것만 센다 — 꺼 둔 초안까지 세면 잔소리가 되고,
 * 잔소리가 되면 목록을 아무도 안 본다(이 저장소가 이미 당한 자리다).
 */
export function testEventVisible(e: { title?: string; visible?: boolean }): boolean {
  if (e.visible === false) return false;
  return looksLikeTestTitle(e.title);
}
